import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FileText, Loader2, Undo2, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  useRollbackReferenceData,
  useRecordVersions,
  useRollbackReferenceDataRecord,
} from "@/hooks/useApi";
import type { ReferenceDataVersion } from "@/types/reference-data";
import { formatRelative } from "@/lib/datetime";
import { errMessage } from "@/lib/reference-data-edit";
import { keyLabel, rollbackOptions, versionLabel } from "@/lib/reference-data-row-history";
import { downloadTemplateCsv, downloadTemplateXlsx } from "@/lib/reference-data-download";

export function RollbackDialog({
  tableName,
  open,
  onOpenChange,
  candidates,
  hasDraft,
}: {
  tableName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: ReferenceDataVersion[];
  hasDraft: boolean;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const rollbackMutation = useRollbackReferenceData(tableName);

  // Reset the selection whenever the dialog reopens or the list changes.
  const firstId = candidates[0]?.ingest_run_id ?? null;
  useEffect(() => {
    if (open) setSelected(firstId);
  }, [open, firstId]);

  const handleConfirm = async () => {
    if (!selected) return;
    try {
      const res = await rollbackMutation.mutateAsync(selected);
      toast.success(
        t("Rolled back · {{a}} rows activated, {{d}} demoted.", {
          a: res.activated_rows,
          d: res.demoted_rows,
        }),
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(errMessage(err, t("Rollback failed.")));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t("Roll back to a previous version")}</DialogTitle>
          <DialogDescription>
            {t(
              "Pick a version that was live before. Rollback re-points the active pointer to that immutable version — nothing is overwritten, and in-flight runs stay pinned to the current one.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[320px] overflow-y-auto -mx-1 px-1 space-y-2 py-1">
          {candidates.length === 0 ? (
            <div className="py-8 px-2 text-center text-sm text-[#7b8799] space-y-2">
              <p>
                {t(
                  "The active version is already the earliest one — there's nothing older to roll back to.",
                )}
              </p>
              {hasDraft && (
                <p className="text-[#596b85]">
                  {t(
                    "There is a newer draft in the list. To make it active, use the Promote button on its row (a draft is promoted, not rolled back to).",
                  )}
                </p>
              )}
            </div>
          ) : (
            candidates.map((v) => {
              const isSel = selected === v.ingest_run_id;
              return (
                <button
                  key={v.ingest_run_id}
                  type="button"
                  onClick={() => setSelected(v.ingest_run_id)}
                  className={`w-full text-left rounded-lg border px-3.5 py-3 transition-colors ${
                    isSel
                      ? "border-[#ffc83d] bg-[#fffdf5]"
                      : "border-[#e8ebf0] hover:border-[#08254c]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-3.5 h-3.5 rounded-full border shrink-0 ${
                        isSel ? "border-[#ffc83d] bg-[#ffc83d]" : "border-[#b2bccb]"
                      }`}
                    />
                    <span className="mono text-[13px] text-[#08254c]">
                      {v.versions.join(", ") || t("(unversioned)")}
                    </span>
                    <span className="mono muted text-[11px] ml-auto">
                      {formatRelative(v.created_at)}
                    </span>
                  </div>
                  <div className="mono muted text-[11px] mt-1 pl-[22px]">
                    {v.row_count.toLocaleString()} {t("rows")}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cancel")}
          </Button>
          <Button
            variant="navy"
            onClick={handleConfirm}
            disabled={!selected || rollbackMutation.isPending}
            className="gap-1.5"
          >
            {rollbackMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Undo2 className="w-3.5 h-3.5" />
            )}
            {t("Roll back to selected")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TemplateDialog({
  open,
  onOpenChange,
  tableName,
  columns,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableName: string;
  columns: string[];
}) {
  const { t } = useTranslation();
  const disabled = columns.length === 0;

  const choose = (fmt: "csv" | "xlsx") => {
    if (fmt === "csv") downloadTemplateCsv(tableName, columns);
    else downloadTemplateXlsx(tableName, columns);
    onOpenChange(false);
  };

  const options: { fmt: "csv" | "xlsx"; icon: React.ReactNode; label: string; sub: string }[] = [
    { fmt: "csv", icon: <FileText className="w-6 h-6" />, label: "CSV", sub: t("Comma-separated values") },
    { fmt: "xlsx", icon: <FileSpreadsheet className="w-6 h-6" />, label: "XLSX", sub: t("Excel workbook") },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-brand text-[#08254c]">{t("Download template")}</DialogTitle>
          <DialogDescription className="text-[#596b85] leading-relaxed">
            {t(
              "An empty file with just the header row — the accepted column names for this source. Fill in your rows and upload it back.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          {options.map((o) => (
            <button
              key={o.fmt}
              type="button"
              onClick={() => choose(o.fmt)}
              disabled={disabled}
              className="flex flex-col items-center gap-2 rounded-lg border border-[#e8ebf0] px-4 py-5 text-[#08254c] transition-colors hover:border-[#ffc83d] hover:bg-[#fffdf5] disabled:opacity-50 disabled:pointer-events-none"
            >
              <span className="text-[#ffc83d]">{o.icon}</span>
              <span className="font-brand font-bold text-[15px]">{o.label}</span>
              <span className="mono muted text-[11px]">{o.sub}</span>
            </button>
          ))}
        </div>
        <p className="mono muted text-[11px] px-1">
          {tableName}_template.{"{csv,xlsx}"}
        </p>
      </DialogContent>
    </Dialog>
  );
}

/** Styled confirm modal (replaces window.confirm) built on the shadcn
 *  AlertDialog. Stays open while `loading`; the caller closes it on done. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmIcon,
  onConfirm,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  confirmIcon?: React.ReactNode;
  onConfirm: () => void;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-brand text-[#08254c]">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-[#596b85] leading-relaxed">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{t("Cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={loading}
            className="bg-[#ffc83d] text-[#08254c] hover:bg-[#ffc83d]/90 font-semibold gap-1.5"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : confirmIcon}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Revert ONE row to an earlier version of its natural key (S-046).
 *
 *  Only versions whose run reached active are offered — the API filters them, so
 *  a never-promoted draft can't be published from here (it would bypass the
 *  ingest sanity gate). Reverting touches only this key; every other row stays
 *  where it is.
 */
export function RecordRollbackDialog({
  tableName,
  recordId,
  onOpenChange,
}: {
  tableName: string;
  recordId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isLoading } = useRecordVersions(tableName, recordId);
  const rollbackMutation = useRollbackReferenceDataRecord(tableName);

  const options = useMemo(() => rollbackOptions(data?.items ?? []), [data]);
  const naturalKey = data?.natural_key ?? [];
  const active = (data?.items ?? []).find((i) => i.is_active);

  // Reset the selection whenever the dialog opens on another row or the list
  // changes — otherwise a stale id from the previous row stays selected.
  const firstId = options[0]?.id ?? null;
  useEffect(() => {
    setSelected(firstId);
  }, [firstId, recordId]);

  const handleConfirm = async () => {
    if (!recordId || !selected) return;
    try {
      const res = await rollbackMutation.mutateAsync({
        recordId,
        targetRowId: selected,
      });
      // A reversão cria uma versão NOVA com o conteúdo da antiga (S-050), então
      // dizer só "revertido para v1.0" esconderia que a linha agora está em v4.0 —
      // o número que aparece na tabela e no histórico.
      toast.success(
        res.copied_from_version && res.to_version
          ? t("Row reverted to the content of v{{from}} — now v{{to}}.", {
              from: res.copied_from_version,
              to: res.to_version,
            })
          : t("Row reverted to v{{v}}.", { v: res.to_version ?? "?" }),
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(errMessage(err, t("Revert failed.")));
    }
  };

  return (
    <Dialog open={!!recordId} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{t("Revert this row")}</DialogTitle>
          <DialogDescription>
            {t(
              "Pick an earlier version of this row. Only this key changes — every other row stays as it is, and nothing is overwritten.",
            )}
          </DialogDescription>
        </DialogHeader>

        {active && naturalKey.length > 0 && (
          <div className="mono text-[11px] text-[#596b85] bg-[#f7f9fc] rounded-md px-3 py-2">
            {keyLabel(naturalKey, active)}
            <span className="ml-2 text-[#7b8799]">
              · {t("now")} {versionLabel(active, t)}
            </span>
          </div>
        )}

        <div className="max-h-[320px] overflow-y-auto -mx-1 px-1 space-y-2 py-1">
          {isLoading ? (
            <div className="py-8 text-center text-[#7b8799]">
              <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
              {t("Loading versions…")}
            </div>
          ) : options.length === 0 ? (
            <div className="py-8 px-2 text-center text-sm text-[#7b8799]">
              {t(
                "This row has no earlier version that was ever active — there is nothing to revert to.",
              )}
            </div>
          ) : (
            options.map((item) => {
              const isSel = selected === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelected(item.id)}
                  className={`w-full text-left rounded-lg border px-3.5 py-3 transition-colors ${
                    isSel
                      ? "border-[#ffc83d] bg-[#fffdf5]"
                      : "border-[#e8ebf0] hover:border-[#08254c]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-3.5 h-3.5 rounded-full border shrink-0 ${
                        isSel ? "border-[#ffc83d] bg-[#ffc83d]" : "border-[#b2bccb]"
                      }`}
                    />
                    <span className="mono text-[13px] text-[#08254c]">
                      {versionLabel(item, t)}
                    </span>
                    <span className="mono muted text-[11px] ml-auto">
                      {formatRelative(item.promoted_at ?? item.run_created_at)}
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cancel")}
          </Button>
          <Button
            variant="navy"
            onClick={handleConfirm}
            disabled={!selected || rollbackMutation.isPending}
            className="gap-1.5"
          >
            {rollbackMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Undo2 className="w-3.5 h-3.5" />
            )}
            {t("Revert to selected")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
